/**
 * PointSymbolPicker — searchable symbol grid for the Vector ToolCard.
 *
 * Replaces the old 12-entry dropdown with the built-in shapes plus the ~770
 * real POI icons from `pointIconRegistry` (Maki + Temaki). The icon sets weigh
 * ~500 KB of JSON, so they are fetched on mount rather than bundled into the
 * initial chunk, and the built-in shapes stay usable while that is in flight.
 *
 * Results are capped by `searchPointIcons` so a broad query cannot mount
 * hundreds of nodes; the hidden count is surfaced so the cap is never silent.
 */
import { useEffect, useId, useMemo, useState } from 'react';
import { Icon, Spinner, Stack, Text, tokens } from '@/design';
import {
  isPointIconId,
  loadPointIcons,
  peekPointIcon,
  pointIconDataUrl,
  searchPointIcons,
  svgDataUrl,
  type PointIconEntry,
} from './pointIconRegistry';
import { pointSymbolPreviewSvg } from './pointSymbolShape';
import { VECTOR_ICON_SWATCHES } from './vectorStyleModel';

export interface PointSymbolPickerProps {
  readonly value: string;
  readonly readOnly: boolean;
  readonly onChange: (symbolId: string) => void;
  readonly previewColor?: string;
  readonly 'data-testid'?: string;
}

/** Swatch tint for the grid previews; the map uses the layer's fill colour. */
const PREVIEW_COLOR = tokens.color.ink.primary;
/** Preview glyph edge length, and the grid cell width it has to fit inside. */
const PREVIEW_SIZE = tokens.space[5];
const CELL_MIN_WIDTH = tokens.space[8];

export function PointSymbolPicker({
  value,
  readOnly,
  onChange,
  previewColor = PREVIEW_COLOR,
  'data-testid': testId = 'point-symbol-picker',
}: PointSymbolPickerProps) {
  const searchId = useId();
  const gridId = useId();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [icons, setIcons] = useState<ReadonlyArray<PointIconEntry>>([]);
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');

  useEffect(() => {
    let cancelled = false;
    void loadPointIcons()
      .then((entries) => {
        if (cancelled) return;
        setIcons(entries);
        setStatus('ready');
      })
      .catch(() => {
        if (cancelled) return;
        setStatus('error');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const shapeMatches = useMemo(() => {
    const terms = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
    if (terms.length === 0) return VECTOR_ICON_SWATCHES;
    return VECTOR_ICON_SWATCHES.filter((swatch) =>
      terms.every((term) => `${swatch.label} ${swatch.id}`.toLowerCase().includes(term)),
    );
  }, [query]);

  const iconMatches = useMemo(() => searchPointIcons(icons, query), [icons, query]);
  const shownCount = shapeMatches.length + iconMatches.visible.length;
  const totalCount = shapeMatches.length + iconMatches.total;

  const currentPreview = useMemo(() => {
    if (isPointIconId(value)) {
      const entry = peekPointIcon(value) ?? icons.find((icon) => icon.id === value);
      if (entry) return pointIconDataUrl(entry, previewColor);
    }
    return svgDataUrl(pointSymbolPreviewSvg(value, PREVIEW_SIZE, previewColor));
  }, [icons, previewColor, value]);

  const pick = (symbolId: string) => {
    onChange(symbolId);
    setOpen(false);
    setQuery('');
  };

  return (
    <Stack direction="column" gap={2} data-testid={testId}>
      <Stack direction="row" align="center" gap={2}>
        <span style={labelStyle()}>Symbol</span>
        <button
          type="button"
          aria-label="Point symbol"
          aria-expanded={open}
          aria-haspopup="dialog"
          disabled={readOnly}
          onClick={() => setOpen((current) => !current)}
          data-testid={`${testId}-toggle`}
          style={toggleStyle(readOnly)}
        >
          <img
            src={currentPreview}
            alt=""
            aria-hidden="true"
            width={PREVIEW_SIZE}
            height={PREVIEW_SIZE}
            style={{ display: 'block' }}
          />
        </button>
      </Stack>

      {open ? (
        <>
          <label htmlFor={searchId} style={labelStyle()}>
            Point symbol
          </label>
          <input
            id={searchId}
            type="search"
            placeholder="Search symbols — e.g. hospital, park, ferry"
            aria-describedby={`${searchId}-count`}
            value={query}
            disabled={readOnly}
            onChange={(event) => setQuery(event.target.value)}
            data-testid={`${testId}-search`}
            style={inputStyle()}
          />

          <Stack direction="row" align="center" gap={2}>
            <Text
              variant="caption"
              as="span"
              id={`${searchId}-count`}
              data-testid={`${testId}-count`}
              style={{ color: tokens.color.ink.secondary }}
            >
              {status === 'loading'
                ? 'Loading icon sets…'
                : status === 'error'
                  ? 'Icon sets unavailable — basic shapes only.'
                  : `${totalCount} symbols found${
                      totalCount > shownCount ? ` · showing first ${shownCount}` : ''
                    }`}
            </Text>
            {status === 'loading' ? <Spinner size="sm" aria-label="Loading icon sets" /> : null}
          </Stack>

          {shownCount === 0 ? (
            <Stack direction="row" align="center" gap={2} data-testid={`${testId}-empty`}>
              <Icon name="search" size={20} decorative />
              <Text variant="caption" as="span">
                No symbol matches “{query}”.
              </Text>
            </Stack>
          ) : (
            <div
              role="radiogroup"
              id={gridId}
              aria-label="Point symbol"
              data-testid={`${testId}-grid`}
              style={gridStyle()}
            >
              {shapeMatches.map((swatch) => (
                <SymbolCell
                  key={swatch.id}
                  label={swatch.label}
                  hint={`Basic shape · ${swatch.label}`}
                  selected={swatch.id === value}
                  disabled={readOnly}
                  onSelect={() => pick(swatch.id)}
                >
                  <img
                    src={svgDataUrl(pointSymbolPreviewSvg(swatch.id, PREVIEW_SIZE, previewColor))}
                    alt=""
                    aria-hidden="true"
                    width={PREVIEW_SIZE}
                    height={PREVIEW_SIZE}
                    style={{ display: 'block' }}
                  />
                </SymbolCell>
              ))}
              {iconMatches.visible.map((entry) => (
                <SymbolCell
                  key={entry.id}
                  label={entry.label}
                  hint={`${entry.setLabel} · ${entry.name}`}
                  selected={entry.id === value}
                  disabled={readOnly}
                  onSelect={() => pick(entry.id)}
                >
                  <img
                    src={pointIconDataUrl(entry, previewColor)}
                    alt=""
                    aria-hidden="true"
                    width={PREVIEW_SIZE}
                    height={PREVIEW_SIZE}
                    style={{ display: 'block' }}
                  />
                </SymbolCell>
              ))}
            </div>
          )}
        </>
      ) : null}
    </Stack>
  );
}

interface SymbolCellProps {
  readonly label: string;
  readonly hint: string;
  readonly selected: boolean;
  readonly disabled: boolean;
  readonly onSelect: () => void;
  readonly children: React.ReactNode;
}

function SymbolCell({ label, hint, selected, disabled, onSelect, children }: SymbolCellProps) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      aria-label={label}
      title={hint}
      disabled={disabled}
      onClick={onSelect}
      style={{
        appearance: 'none',
        display: 'inline-flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'flex-start',
        gap: tokens.space[1],
        padding: tokens.space[1],
        minHeight: tokens.density.touchMin,
        background: selected ? tokens.color.surface[3] : tokens.color.surface[2],
        border: selected
          ? `${tokens.border.width.thin}px solid ${tokens.color.accent.primary}`
          : `${tokens.border.width.thin}px solid ${tokens.color.surface[3]}`,
        borderRadius: tokens.radius.md,
        color: tokens.color.ink.primary,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.6 : 1,
        overflow: 'hidden',
      }}
    >
      {children}
      <span
        style={{
          fontFamily: tokens.font.family.ui,
          fontSize: tokens.font.size.xs,
          color: tokens.color.ink.secondary,
          maxWidth: '100%',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {label}
      </span>
    </button>
  );
}

function gridStyle() {
  return {
    display: 'grid',
    gridTemplateColumns: `repeat(auto-fill, minmax(${CELL_MIN_WIDTH}px, 1fr))`,
    gap: tokens.space[1],
    // The dock is a narrow rail, so the grid scrolls rather than pushing the
    // rest of the card off-screen.
    maxHeight: tokens.size.dropdownMaxHeight,
    overflowY: 'auto',
    padding: tokens.space[1],
    background: tokens.color.surface[1],
    border: `${tokens.border.width.thin}px solid ${tokens.color.surface[3]}`,
    borderRadius: tokens.radius.md,
  } as const;
}

function labelStyle() {
  return {
    fontFamily: tokens.font.family.ui,
    fontSize: tokens.font.role.label.size,
    fontWeight: tokens.font.role.label.weight,
    color: tokens.color.ink.secondary,
  } as const;
}

function toggleStyle(disabled: boolean) {
  return {
    appearance: 'none',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: tokens.density.touchMin,
    height: tokens.density.touchMin,
    padding: tokens.space[1],
    background: tokens.color.surface[2],
    border: `${tokens.border.width.thin}px solid ${tokens.color.surface[3]}`,
    borderRadius: tokens.radius.md,
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.6 : 1,
  } as const;
}

function inputStyle() {
  return {
    minHeight: tokens.density.touchMin,
    background: tokens.color.surface[2],
    color: tokens.color.ink.primary,
    border: `${tokens.border.width.thin}px solid ${tokens.color.surface[3]}`,
    borderRadius: tokens.radius.input,
    paddingInline: tokens.space[2],
    fontFamily: tokens.font.family.ui,
    fontSize: tokens.font.size.sm,
  } as const;
}
